import baseApiSlice from 'api/baseApiSlice';
import { type CharityDocument } from 'features/documents/types';

import { DocumentsTags } from './documentsTags';
import { toCharityDocument } from './serializers';
import {
  type DeleteDocumentRequest,
  type DocumentSummaryItem,
  type DownloadDocumentRequest,
  type DownloadDocumentResponse,
  type GetDocumentsSummaryRequest,
  type GetDocumentsSummaryResponse,
  type GetUploadOptionsRequest,
  type GetUploadOptionsResponse,
  type ListDocumentsRequest,
  type ListDocumentsResponse,
  type ListDocumentsResult,
  type MarkDocumentsSeenRequest,
  type UpdateDocumentRequest,
  type UploadDocumentRequest,
  type UploadDocumentResponse,
  type UploadOptions,
} from './types';

const documentsAPISlice = baseApiSlice
  .enhanceEndpoints({
    addTagTypes: Object.values(DocumentsTags),
  })
  .injectEndpoints({
    endpoints: (build) => ({
      listDocuments: build.query<ListDocumentsResult, ListDocumentsRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/list',
          method: 'POST',
          data: payload,
        }),
        transformResponse: (response: ListDocumentsResponse): ListDocumentsResult => ({
          paging: response.paging,
          documents: response.data.map(toCharityDocument),
        }),
        providesTags: [DocumentsTags.Documents],
      }),
      getDocumentsSummary: build.query<DocumentSummaryItem[], GetDocumentsSummaryRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/summary',
          method: 'POST',
          data: payload,
        }),
        transformResponse: (response: GetDocumentsSummaryResponse) => response.data,
        providesTags: [DocumentsTags.Summary],
      }),
      markDocumentsSeen: build.mutation<void, MarkDocumentsSeenRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/mark-seen',
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: [DocumentsTags.Summary],
      }),
      downloadDocument: build.mutation<DownloadDocumentResponse['data'], DownloadDocumentRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/download',
          method: 'POST',
          data: payload,
        }),
        transformResponse: (response: DownloadDocumentResponse) => response.data,
      }),
      getUploadOptions: build.query<UploadOptions, GetUploadOptionsRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/upload-options',
          method: 'POST',
          data: payload,
        }),
        transformResponse: (response: GetUploadOptionsResponse) => response.data,
      }),
      uploadDocument: build.mutation<CharityDocument, UploadDocumentRequest>({
        query: ({ file, metadata }) => {
          const data = new FormData();
          data.append('file', file);
          data.append(
            'request',
            new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
          );
          return { url: '/charity/documents/v1/upload', method: 'POST', data };
        },
        transformResponse: (response: UploadDocumentResponse) => toCharityDocument(response.data),
        invalidatesTags: [DocumentsTags.Documents, DocumentsTags.Summary],
      }),
      deleteDocument: build.mutation<void, DeleteDocumentRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/delete',
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: [DocumentsTags.Documents, DocumentsTags.Summary],
      }),
      updateDocument: build.mutation<void, UpdateDocumentRequest>({
        query: (payload) => ({
          url: '/charity/documents/v1/update',
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: [DocumentsTags.Documents, DocumentsTags.Summary],
      }),
    }),
  });

export default documentsAPISlice;
